import { useEffect, useState } from "react";
import { useSearchParams, useNavigate, useLocation } from "react-router-dom";
import { api } from "@/lib/api";

/**
 * usePaymentStatus — when a `session_id` is present in the URL (Stripe returns
 * there with ?session_id=...), poll the backend until it resolves to `paid`
 * or times out. Returns { state, kind, amount, error } for the caller to show
 * a banner/toast.
 *
 *   state: 'idle' | 'polling' | 'paid' | 'expired' | 'failed' | 'timeout'
 */
export function usePaymentStatus({ onPaid } = {}) {
  const [sp] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const sessionId = sp.get("session_id");

  const [state, setState] = useState("idle");
  const [kind, setKind] = useState(null);
  const [amount, setAmount] = useState(0);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;
    let attempts = 0;
    const MAX = 10; // ~30s
    setState("polling");

    const poll = async () => {
      if (cancelled) return;
      try {
        const { data } = await api.get(`/payments/checkout/status/${sessionId}`);
        if (cancelled) return;
        setKind(data.kind);
        setAmount((data.amount_total || 0) / 100);

        if (data.payment_status === "paid") {
          setState("paid");
          onPaid?.(data);
          // Strip session_id from URL so refresh doesn't repoll
          const clean = new URLSearchParams(location.search);
          clean.delete("session_id");
          navigate(
            { pathname: location.pathname, search: clean.toString() },
            { replace: true }
          );
          return;
        }
        if (data.status === "expired") {
          setState("expired");
          return;
        }
        attempts += 1;
        if (attempts >= MAX) {
          setState("timeout");
          return;
        }
        setTimeout(poll, 3000);
      } catch (e) {
        if (cancelled) return;
        setError(e?.response?.data?.detail || "Error verificando pago");
        setState("failed");
      }
    };

    poll();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  return { state, kind, amount, error, sessionId };
}
